// Adaptador de pedido (spec 2026-10-01 §3.1): a página de pedido e seus componentes falam com
// o backend só por aqui. `grading` é um invólucro fino de products/functions.js (sem mudança de
// comportamento); `extended_art` usa as rotas espelhadas de /admin/extended-art.
import {
    getProduct,
    updateProduct,
    updateProductStatus,
    getProductHistory,
    postNewComment,
    getSpecialPhrase,
    getProductSerials,
    getProductSerialsDrafts,
    presignProductAttachment,
    completeProductAttachment,
    downloadProductAttachment,
    renameProductAttachment,
    deleteProductAttachment,
} from "../products/functions";
import { createSerialFromDraft } from "../serials/functions";
import { getVisibleGradeFields, renderApplicableGradeValue, validateProductAutographNotes } from "../utils/serialGradeFields";
import { getScoreTitle } from "../utils/gradingTypes";
import {
    getExtendedArtOrder,
    updateExtendedArtOrder,
    updateExtendedArtOrderStatus,
    restoreExtendedArtHistory,
    postExtendedArtComment,
    presignExtendedArtAttachment,
    completeExtendedArtAttachment,
    downloadExtendedArtAttachment,
    renameExtendedArtAttachment,
    deleteExtendedArtAttachment,
    extendedArtErrorMessage,
    extendedArtOutMessage,
    isRawNetworkError,
    EXTENDED_ART_MESSAGE_TRANSLATIONS,
} from "../extendedArt/functions";
import { conditionLabel } from "../extendedArt/conditions";
import { EXTENDED_ART_KIND, GRADING_KIND } from "../extendedArt/kind";

const LOAD_ERROR = "Erro ao obter dados do pedido";
// Textos fixos de hoje: productHistory.js ("restaurar") e editModal.js ("atualizar").
const RESTORE_ERROR = "Erro ao restaurar versão";
const UPDATE_ERROR = "Erro ao atualizar pedido";

// ---------- grading ----------

// Movido literalmente de productSerials.js (copyGradesToClipboard), inclusive o espaço depois de `---`.
const gradingCopyText = (productSerials, productData) => productSerials?.map(ps => {
    const visibleGrades = getVisibleGradeFields(ps, productData);
    const gradeLines = [
        ...visibleGrades.subgrades.map(field => `${field.label}: ${renderApplicableGradeValue(field.value)}`),
        ...(visibleGrades.autographNote ? [`${visibleGrades.autographNote.label}: ${renderApplicableGradeValue(visibleGrades.autographNote.value)}`] : []),
        ...(visibleGrades.generalScore ? [`Nota Final: ${renderApplicableGradeValue(visibleGrades.generalScore.value)} (${getScoreTitle(visibleGrades.generalScore.value) || "-"})`] : [])
    ];
    return `
            ${ps?.serialNumber}\n
            ${ps?.gradableModel?.name} ${ps?.gradableModel?.numbering}
            ${gradeLines.join("\n            ")}\n
            --- 
            `
})?.join("");

// Movido de productHistory.js (restoreVersion).
const gradingRestoreBody = (his) => ({
    gradingType: his?.gradingType,
    numberOfCards: his?.numberOfCards,
    priority: his?.priority,
    deadline: his?.deadline,
    delivery: his?.delivery,
    finalPrice: his?.finalPrice,
    cardsValue: his?.cardsValue,
    insurance: his?.insurance,
    shipping: his?.shipping,
    shippingService: his?.shippingService,
    store: his?.store,
    postalCode: his?.postalCode,
    street: his?.street,
    district: his?.district,
    city: his?.city,
    state: his?.state,
    country: his?.country,
    houseNumber: his?.houseNumber,
    complement: his?.complement,
    status: his?.status
});

// Movido de productInfo.js (doUpdateStatus, ramo GRADED): badToast + return viram { ok: false, message }.
const gradingBeforeGraded = async (token, productData) => {
    const productSerials = Array.isArray(productData?.serials) ? productData.serials : [];
    if (productSerials.length < productData?.numberOfCards) {
        return { ok: false, message: "Seriais não preenchidos!" };
    }
    const autographNotesValidation = validateProductAutographNotes({ ...productData, serials: productSerials });
    if (!autographNotesValidation.ok) {
        const messages = [];
        if (autographNotesValidation.missingCount > 0) {
            messages.push(`${autographNotesValidation.missingCount} ${autographNotesValidation.missingCount === 1 ? "serial está sem nota de autógrafo" : "seriais estão sem nota de autógrafo"}`);
        }
        if (autographNotesValidation.invalidCount > 0) {
            messages.push(`${autographNotesValidation.invalidCount} ${autographNotesValidation.invalidCount === 1 ? "serial tem nota de autógrafo inválida" : "seriais têm nota de autógrafo inválida"}`);
        }
        return { ok: false, message: `Revise as notas de autógrafo antes de concluir a graduação: ${messages.join(" e ")}.` };
    }
    const newSerials = productSerials.filter(d => d?.isDraft);
    for (let i = 0; i < newSerials?.length; i++) {
        const current = newSerials[i];
        const gradedData = await createSerialFromDraft(token, current?._id);
        if (gradedData?.type === "error") {
            return { ok: false, message: gradedData?.message };
        }
    }
    return { ok: true };
};

const gradingAdapter = {
    kind: GRADING_KIND,
    title: "Pedido",
    itemsTitle: "Seriais do pedido",
    load: (token, id) => getProduct(token, id),
    loadErrorMessage: () => LOAD_ERROR,
    update: (token, id, patch) => updateProduct(token, patch, id),
    updateStatus: (token, id, { status, notify, statusMessage, returnCode, trackingCode, userEmail }) =>
        updateProductStatus(token, id, status, notify, statusMessage, returnCode, userEmail, trackingCode),
    beforeGraded: gradingBeforeGraded,
    history: (token, id) => getProductHistory(token, id),
    restore: (token, id, entry) => updateProduct(token, gradingRestoreBody(entry), entry?.product),
    restoreErrorMessage: () => RESTORE_ERROR,
    updateErrorMessage: () => UPDATE_ERROR,
    comments: { post: (token, id, text) => postNewComment(token, id, text) },
    attachments: {
        presign: (token, id, body) => presignProductAttachment(token, id, body),
        complete: (token, id, attachmentId) => completeProductAttachment(token, id, attachmentId),
        download: (token, id, attachmentId) => downloadProductAttachment(token, id, attachmentId),
        rename: (token, id, attachmentId, name) => renameProductAttachment(token, id, attachmentId, name),
        remove: (token, id, attachmentId) => deleteProductAttachment(token, id, attachmentId),
    },
    items: {
        // Igual ao productSerials.js anterior: seriais + rascunhos, sem guarda de erro.
        list: async (token, order) => {
            const data = await getProductSerials(token, order?._id);
            const data2 = await getProductSerialsDrafts(token, order?._id);
            return { type: "success", data: [...data?.data, ...data2?.data?.filter(d => d?.isDraft)] };
        },
        copyText: gradingCopyText,
        canGenerateTags: (items, order) => !(items?.length < order?.numberOfCards),
        tagsNavigation: (order) => ({ origin: "products", products: [order?._id] }),
    },
    specialPhrase: (token, id) => getSpecialPhrase(token, id),
    errorMessage: (out, fallback) => out?.message || fallback,
    shippingQuoteGradingType: null,
    capabilities: { gradingType: true, preLacre: true, specialPhrase: true, payment: true, coupon: true, itemKind: "serial" },
};

// ---------- extended_art ----------

const storeRef = (store) => {
    if (store && typeof store === "object") return store;
    return store ? { _id: String(store) } : null;
};

const byLabel = (by) => {
    if (by && typeof by === "object") return by.email || by.name || "admin";
    return by ? "admin" : "sistema";
};

/** Deixa o ExtendedArtOrder no formato que os componentes do pedido normal leem (decisão 6). */
export const normalizeExtendedOrder = (order) => {
    if (!order) return order;
    const user = order.user && typeof order.user === "object"
        ? { ...order.user, comments: order.user.comments || [], documentNumber: order.user.documentNumber ?? order.user.document }
        : order.user;
    let comments = [];
    if (Array.isArray(order.comments)) comments = order.comments;
    else if (order.comments) comments = [{ date: order.createdAt, text: String(order.comments) }];
    return { ...order, user, store: storeRef(order.store), comments, attachments: order.attachments || [] };
};

/**
 * Retratos "antes da alteração" (formato do histórico do pedido normal), do mais novo para o
 * mais antigo. Cada retrato é o pedido atual com os `prev` só daquela entrada — o mesmo que o
 * restore do backend aplica (§4), sem acumular as entradas mais novas.
 */
export const normalizeExtendedHistory = (order) => {
    const entries = order?.history || [];
    const out = [];
    for (let index = entries.length - 1; index >= 0; index -= 1) {
        const entry = entries[index];
        const fields = entry?.fields || {};
        const prev = {};
        Object.entries(fields).forEach(([field, change]) => {
            prev[field] = field === "store" ? storeRef(change?.prev) : change?.prev;
        });
        out.push({ ...order, ...prev, createdAt: entry?.at, alteredBy: { email: byLabel(entry?.by) }, index, changedFields: Object.keys(fields) });
    }
    return out;
};

// Enum de notaAutografo do serial (contrato 00), igual ao FinalizeItemModal.
const AUTOGRAPH_NOTES = ["Autentico", "8", "9", "10"];

const isFinalized = (item, order) =>
    !item?.isDraft && Boolean(item?.serialNumber) && Boolean(item?.condition)
    && (!order?.autographEvaluation || AUTOGRAPH_NOTES.includes(item?.notaAutografo));

/** Erro com o código/texto conhecido do estendido; senão o texto fixo da página normal. */
const extendedFixedErrorMessage = (out, fixed) =>
    isRawNetworkError(out) ? fixed : extendedArtErrorMessage(out?.code, EXTENDED_ART_MESSAGE_TRANSLATIONS[out?.message] ? out.message : fixed);

const extendedCopyText = (items, order) => (items || []).map((item) => [
    item?.serialNumber || "rascunho",
    `${item?.gradableModel?.name || ""} ${item?.gradableModel?.numbering || ""}`.trim(),
    `Condição: ${item?.condition ? `${item.condition} — ${conditionLabel(item.condition)}` : "-"}`,
    ...(order?.autographEvaluation ? [`Autógrafo: ${item?.notaAutografo ?? "-"}`] : []),
    "---",
].join("\n")).join("\n");

const extendedAdapter = {
    kind: EXTENDED_ART_KIND,
    title: "Pedido de arte estendida",
    itemsTitle: "Itens do pedido",
    load: async (token, id) => {
        const out = await getExtendedArtOrder(token, id);
        if (out?.type !== "success") return out;
        return { ...out, data: normalizeExtendedOrder(out.data?.order) };
    },
    loadErrorMessage: (out) => extendedArtOutMessage(out, LOAD_ERROR),
    update: (token, id, patch) => updateExtendedArtOrder(token, id, patch),
    updateStatus: (token, id, body) => updateExtendedArtOrderStatus(token, id, body),
    beforeGraded: async (token, order) => {
        const items = Array.isArray(order?.serials) ? order.serials : [];
        const done = items.length >= (order?.numberOfCards || 0) && items.every((item) => isFinalized(item, order));
        return done ? { ok: true } : { ok: false, message: extendedArtErrorMessage("ITEMS_NOT_FINALIZED") };
    },
    history: async (token, id) => {
        const out = await getExtendedArtOrder(token, id);
        if (out?.type !== "success") return out;
        return { type: "success", data: normalizeExtendedHistory(normalizeExtendedOrder(out.data?.order)) };
    },
    restore: (token, id, entry) => restoreExtendedArtHistory(token, id, entry?.index),
    restoreErrorMessage: (out) => extendedFixedErrorMessage(out, RESTORE_ERROR),
    updateErrorMessage: (out) => extendedFixedErrorMessage(out, UPDATE_ERROR),
    comments: { post: (token, id, text) => postExtendedArtComment(token, id, text) },
    attachments: {
        presign: (token, id, body) => presignExtendedArtAttachment(token, id, body),
        complete: (token, id, attachmentId) => completeExtendedArtAttachment(token, id, attachmentId),
        download: (token, id, attachmentId) => downloadExtendedArtAttachment(token, id, attachmentId),
        rename: (token, id, attachmentId, name) => renameExtendedArtAttachment(token, id, attachmentId, name),
        remove: (token, id, attachmentId) => deleteExtendedArtAttachment(token, id, attachmentId),
    },
    items: {
        list: async (token, order) => {
            const out = await getExtendedArtOrder(token, order?._id);
            if (out?.type !== "success") return out;
            return { type: "success", data: out.data?.serials || [] };
        },
        copyText: extendedCopyText,
        canGenerateTags: (items, order) =>
            (items?.length || 0) > 0 && items.length >= (order?.numberOfCards || 0) && items.every((i) => !i?.isDraft && i?.serialNumber),
        tagsNavigation: (order) => ({ origin: "extended-art", extendedOrders: [order?._id] }),
    },
    specialPhrase: null,
    errorMessage: (out, fallback) => extendedArtOutMessage(out, fallback),
    // getFrete exige gradingType; a caixa da arte estendida é a do standard (decisão 9).
    shippingQuoteGradingType: "standard",
    capabilities: { gradingType: false, preLacre: false, specialPhrase: false, payment: false, coupon: false, itemKind: "extended_art" },
};

export const getOrderAdapter = (kind) => (kind === EXTENDED_ART_KIND ? extendedAdapter : gradingAdapter);
